import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { createColumnHelper } from "@tanstack/react-table";
import { UsersRound } from "lucide-react";
import { useState } from "react";
import { ApiError, errorMessage } from "../../api/client";
import { ops, qk, staffQuery } from "../../api/ops";
import type { OpsStaffMember } from "../../api/types";
import { useMe } from "../../auth/useMe";
import { DataTable } from "../../components/DataTable";
import { RequirePasskey } from "../../components/RequirePasskey";
import { toast } from "../../components/toast";
import {
  Badge,
  Button,
  Card,
  Dialog,
  EmptyState,
  ErrorState,
  Input,
  Loading,
  PageHeader,
} from "../../components/ui";
import { fmtDate, relTime } from "../../lib/format";

export const Route = createFileRoute("/_app/staff")({
  staticData: { crumb: { label: "Staff", parents: [] } },
  loader: ({ context }) => context.queryClient.prefetchQuery(staffQuery),
  component: StaffPage,
});

const col = createColumnHelper<OpsStaffMember>();
const ASK_ANOTHER = "Ask another operator to remove you.";

function StaffPage() {
  const me = useMe();
  const qc = useQueryClient();
  const q = useQuery(staffQuery);
  const [email, setEmail] = useState("");
  const [removing, setRemoving] = useState<string | null>(null);

  const add = useMutation({
    mutationFn: (e: string) => ops.addStaff(e),
    onSuccess: (list, e) => {
      qc.setQueryData(qk.staff, list);
      setEmail("");
      toast(`${e} is Kite staff.`);
    },
    onError: (e) => toast(errorMessage(e), "danger"),
  });
  const remove = useMutation({
    mutationFn: (e: string) => ops.removeStaff(e),
    onSuccess: (list, e) => {
      qc.setQueryData(qk.staff, list);
      setRemoving(null);
      toast(`${e} removed.`);
    },
    onError: (e) => {
      setRemoving(null);
      toast(
        e instanceof ApiError && e.code === "cannot_remove_self" ? ASK_ANOTHER : errorMessage(e),
        "danger",
      );
    },
  });

  const askRemove = (e: string) => {
    if (e.toLowerCase() === me.email.toLowerCase()) toast(ASK_ANOTHER, "danger");
    else setRemoving(e);
  };

  // Rebuilt each render: the Remove cells read who is signed in.
  const columns = [
    col.accessor("email", {
      header: "Email",
      cell: (c) => (
        <span className="flex items-center gap-2">
          <span className="font-medium">{c.getValue()}</span>
          {c.getValue() === me.email ? <Badge tone="neutral">You</Badge> : null}
        </span>
      ),
    }),
    col.accessor("role", {
      header: "Role",
      cell: (c) => <span className="capitalize">{c.getValue()}</span>,
    }),
    col.accessor("added_by", {
      header: "Added by",
      cell: (c) => (
        <span className="text-ink-2">{c.getValue() === "cli" ? "Command line" : c.getValue()}</span>
      ),
    }),
    col.accessor("added_at", {
      header: "Added",
      cell: (c) => <span className="whitespace-nowrap text-ink-2">{fmtDate(c.getValue())}</span>,
    }),
    col.accessor((s) => s.last_sign_in_at ?? "", {
      id: "last_sign_in_at",
      header: "Last sign-in",
      cell: (c) => (
        <span className="whitespace-nowrap text-ink-2">{relTime(c.row.original.last_sign_in_at)}</span>
      ),
    }),
    col.display({
      id: "remove",
      cell: (c) => (
        <div className="flex justify-end">
          <Button size="sm" variant="ghost" onClick={() => askRemove(c.row.original.email)}>
            Remove
          </Button>
        </div>
      ),
    }),
  ];

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const v = email.trim();
    if (v) add.mutate(v);
  };

  return (
    <>
      <PageHeader title="Staff" />
      <RequirePasskey />
      <form onSubmit={submit} className="mb-3 flex max-w-md gap-2">
        <Input
          aria-label="Email"
          type="email"
          placeholder="name@vertec.io"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
        />
        <Button type="submit" variant="primary" busy={add.isPending}>
          Add
        </Button>
      </form>
      <Card>
        {q.isPending ? (
          <Loading />
        ) : q.isError ? (
          <ErrorState error={q.error} onRetry={() => q.refetch()} />
        ) : (
          <DataTable
            data={q.data.items}
            columns={columns}
            getRowId={(s) => s.email}
            initialSorting={[{ id: "email", desc: false }]}
            empty={<EmptyState icon={<UsersRound />} title="No staff" />}
          />
        )}
      </Card>
      <Dialog
        open={removing !== null}
        onClose={() => setRemoving(null)}
        title={`Remove ${removing ?? ""}?`}
        description="Their ops sessions end now."
        tone="danger"
        size="sm"
        footer={
          <>
            <Button onClick={() => setRemoving(null)}>Cancel</Button>
            <Button
              variant="danger"
              busy={remove.isPending}
              onClick={() => removing && remove.mutate(removing)}
            >
              Remove
            </Button>
          </>
        }
      />
    </>
  );
}
